import { defineStore } from 'pinia';

import { requestWrapper, unauthorized } from '@/api';
import type { AuthStore, LoginResponse, Token, TokenContent } from '@/types/auth';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@/router';
import { VerifyTFA } from '@/service/tfa';

export const useAuthStore = defineStore('auth', {
  state: (): AuthStore => ({
    // initialize state from local storage to enable user to stay logged in
    token: JSON.parse(localStorage.getItem('token') as string)
  }),
  actions: {
    login: async function(username: string, password: string) {
      return await requestWrapper.post({
        path: `/login`,
        body: { username: username, password: password, grantType: 'password' }
      }).then((response: Response) => {return this.handleResponse(response)});
    },
    verifyTFA: async function(response: LoginResponse) {
      return await VerifyTFA(response, `/login`)
        .then((response: Response) => {
          return this.handleResponse(response);
        });
    },
    tokenLogin: async function() {
      return await requestWrapper.post({
        path: `/login`,
        body: { token: this.refreshString, grantType: 'token' }
      }).then((response: Response) => {return this.handleResponse(response)});
    },
    logout: async function() {
      this.token = null;
      localStorage.removeItem('token');
      if (Router.currentRoute.value.name) {
        this.lastPage = { name: Router.currentRoute.value.name.toString() };
      }
      await Router.push({ name: 'logged-out' });
    },
    store: function(login: LoginResponse) {
      const token = {
        accessToken: login.accessToken,
        refreshToken: login.refreshToken
      } as Token;
      localStorage.setItem('token', JSON.stringify(token));
      this.token = token;
    },
    handleResponse: async function(response: Response) {
      if (!response.ok || unauthorized.includes(response.status)) {
        return Promise.reject(Error(response.statusText));
      }
      return await response.json().then((value: LoginResponse) => {
        if (value.accessToken && value.refreshToken) {
          this.store(value);
        }
        return value;
      });
    }
  },
  getters: {
    access: (state: AuthStore): TokenContent => {
      return state.token != null && state.token.accessToken !== undefined ? jwtDecode(state.token.accessToken) : ({} as TokenContent);
    },
    refresh: (state: AuthStore): TokenContent => {
      return state.token != null && state.token.refreshToken !== undefined ? jwtDecode(state.token.refreshToken) : ({} as TokenContent);
    },
    accessString: (state: AuthStore): string => {
      return state.token != null && state.token.accessToken !== undefined ? state.token.accessToken : '';
    },
    refreshString: (state: AuthStore): string => {
      return state.token != null && state.token.refreshToken !== undefined ? state.token.refreshToken : '';
    }
  }
});

